import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ROUTES } from "../config/routes";
import { PopupCampaignsAdminService } from "../api/generated/services/PopupCampaignsAdminService";
import { PopupCampaignAdminViewModel } from "../api/generated/models/PopupCampaignAdminViewModel";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { Toast } from "../components/Toast";
import { PopupCampaignCard } from "../components/PopupCampaigns/PopupCampaignCard";
import { apiErrorMessage } from "../helpers/popupCampaignForm";
import "../api/openApiConfig";

const AdminPopupCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<PopupCampaignAdminViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = useCallback(async () => {
    try {
      setCampaigns(await PopupCampaignsAdminService.getApiPopupCampaignsAdmin());
    } catch (e) {
      console.error("Error loading pop-up campaigns:", e);
      setError(apiErrorMessage(e, "Failed to load pop-up campaigns"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = campaigns.findIndex((c) => c.id === active.id);
    const newIndex = campaigns.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previous = campaigns;
    const reordered = arrayMove(campaigns, oldIndex, newIndex);
    setCampaigns(reordered);
    try {
      const saved = await PopupCampaignsAdminService.putApiPopupCampaignsAdminReorder({
        orderedIds: reordered.map((c) => c.id!),
      });
      setCampaigns(saved);
    } catch (e) {
      console.error("Error reordering pop-up campaigns:", e);
      setCampaigns(previous);
      setError(apiErrorMessage(e, "Could not save the new order. The list may be out of date — reloading."));
      load();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h2 className="font-medium">Pop-up Campaigns</h2>
          <p className="text-sm text-gray-600 mt-2">
            Full-screen pop-ups shown once per user when they open the app. Order is priority: the first Active
            campaign inside its dates that a user has not seen yet is the one shown.
          </p>
        </div>
        <Button onClick={() => navigate(ROUTES.ADMIN_POPUP_CAMPAIGN_EDIT("new"))}>Create campaign</Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={campaigns.map((c) => c.id!)} strategy={verticalListSortingStrategy}>
          {campaigns.map((campaign) => (
            <PopupCampaignCard
              key={campaign.id}
              campaign={campaign}
              onClick={(c) => navigate(ROUTES.ADMIN_POPUP_CAMPAIGN_EDIT(c.id))}
            />
          ))}
        </SortableContext>
      </DndContext>

      {campaigns.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No pop-up campaigns yet. Click "Create campaign" to add one.
        </div>
      )}

      {error && <Toast message={error} type="error" onClose={() => setError(null)} />}
    </div>
  );
};

export default AdminPopupCampaigns;
